import React, { PropsWithChildren, ReactNode } from 'react';

import { ContentPanel } from '../../../ComponentLibrary/molecules/panels/ContentPanel/ContentPanel';
import { HideSideMenu, UnhideSideMenu } from '../../actions/menuActions';
import { useThrottle } from '../../hooks/useThrottle';
import { SideMenuCondition } from '../../store/reducers/studentSideMenuReducer';
import { useStudentSelector } from '../../store/selectors/useStudentSelector';
import { Body, BodyProps } from './Body/Body';
import { Caption } from './Captions/Captions';
import { DesktopContent, DesktopContentProps } from './Content/DesktopContent';
import { MobileContent } from './Content/MobileContent';
import { Loader } from './Loader/Loader';
import { Modal } from './Modal/Modal';
import { Title, TitleProps } from './Title/Title';
import { Toolbar } from './Toolbar/Toolbar';
import { TopBar, TopBarProps } from './TopBar/TopBar';
import { useDispatch } from 'react-redux';

interface ContentWrapperContextValue {
  isControlVisible: boolean;
  toggleControls: (value: boolean) => void;
}

interface ContentWrapperProps {
  defaultControlVisible?: boolean;
  alwaysShowControl?: boolean;
  children?: ReactNode;
}

interface ContentWrapperCompoundComponent extends React.FC<ContentWrapperProps> {
  Toolbar: typeof Toolbar;
  Title: React.FC<TitleProps>;
  Body: React.FC<BodyProps>;
  Modal: typeof Modal;
  TopBar: React.FC<TopBarProps>;
  DesktopContent: React.FC<DesktopContentProps>;
  MobileContent: React.FC<PropsWithChildren>;
  ContentPanel: typeof ContentPanel;
  Caption: React.FC<PropsWithChildren>;
  Loader: typeof Loader;
}

const CONTROL_VISIBILITY_THROTTLE_DELAY_MS = 500;

const ContentWrapperModalContext = React.createContext<ContentWrapperContextValue>({} as ContentWrapperContextValue);

export const ContentWrapper: ContentWrapperCompoundComponent = ({
  children,
  defaultControlVisible = true,
  alwaysShowControl,
}) => {
  const [isControlVisible, setIsControlVisible] = React.useState(alwaysShowControl || defaultControlVisible);
  const throttledIsControlVisible = useThrottle(isControlVisible, CONTROL_VISIBILITY_THROTTLE_DELAY_MS);
  const sideMenuCondition = useStudentSelector((state) => state.sideMenu.sideMenuCondition);
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (throttledIsControlVisible) {
      if (sideMenuCondition === SideMenuCondition.buttonHidden) {
        dispatch(UnhideSideMenu);
      }
    } else if (sideMenuCondition === SideMenuCondition.collapsed) {
      dispatch(HideSideMenu);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [throttledIsControlVisible]);

  const toggleControls = React.useCallback(
    (value: boolean) => {
      if (!alwaysShowControl) {
        setIsControlVisible(value);
      }
    },
    [alwaysShowControl]
  );

  const value = React.useMemo(
    () => ({ isControlVisible: throttledIsControlVisible, toggleControls }),
    [throttledIsControlVisible, toggleControls]
  );

  return <ContentWrapperModalContext.Provider value={value}>{children}</ContentWrapperModalContext.Provider>;
};

export const useContentWrapperContext = (): ContentWrapperContextValue => React.useContext(ContentWrapperModalContext);

ContentWrapper.Toolbar = Toolbar;
ContentWrapper.Title = Title;
ContentWrapper.Body = Body;
ContentWrapper.Modal = Modal;

ContentWrapper.TopBar = TopBar;

ContentWrapper.MobileContent = MobileContent;
ContentWrapper.DesktopContent = DesktopContent;
ContentWrapper.ContentPanel = ContentPanel;
ContentWrapper.Caption = Caption;
ContentWrapper.Loader = Loader;
